/**
 * PERFORMANCE CONSIDERATIONS FOR LARGE NUMBERS OF NOTES:
 * 
 * Current Implementation:
 * - Uses localStorage (5-10MB limit across all domains)
 * - Stores images as base64 in note content
 * - All notes loaded into memory at once
 * 
 * Potential Issues with 100+ notes with screenshots:
 * 1. Storage Limits: Each screenshot can be 100KB-1MB+ as base64
 *    - 100 notes with 1 screenshot each = potential 100MB+ of data
 *    - Will exceed localStorage limits quickly
 * 
 * 2. Memory Usage: All notes kept in React state
 *    - Can cause browser tab to use excessive memory
 *    - May slow down rendering and interactions
 * 
 * 3. Save Performance: Saving all notes on every change
 *    - Large JSON.stringify operations can block the UI
 * 
 * RECOMMENDED SOLUTIONS:
 * 1. Switch to IndexedDB for storage (50MB+ capacity)
 * 2. Implement lazy loading of note content
 * 3. Add image compression before storing
 * 4. Paginate the notes list (show 20-30 at a time)
 * 5. Store images separately from note content
 * 6. Add a warning when approaching storage limits
 * 7. Implement virtual scrolling for the notes list
 */

import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  FileText, 
  ChevronLeft, 
  ChevronRight, 
  PanelLeftClose, 
  PanelLeft,
  Plus,
  Search,
  MoreVertical,
  Trash2,
  Copy,
  Edit3,
  Folder,
  FolderOpen,
  File,
  Upload,
  Download,
  FolderPlus,
  ArrowLeft,
  ArrowRight,
  Home,
  Bold, 
  Italic, 
  Underline as UnderlineIcon, 
  Link as LinkIcon,
  List,
  ListOrdered,
  CheckSquare,
  AlertCircle,
  Info,
  CheckCircle,
  Search as SearchIcon,
  Code,
  Heading1,
  Heading2,
  Heading3,
  Highlighter,
  Table as TableIcon,
  Maximize2,
  X,
  Undo2,
  Redo2
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { useToast } from "@/components/ui/use-toast";
import { debounce } from "lodash";

// TipTap imports
import { useEditor, EditorContent } from '@tiptap/react';
import { ErrorBoundary } from 'react-error-boundary';
import StarterKit from '@tiptap/starter-kit';
import BulletList from '@tiptap/extension-bullet-list';
import OrderedList from '@tiptap/extension-ordered-list';
import ListItem from '@tiptap/extension-list-item';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Highlight from '@tiptap/extension-highlight';
import { Table } from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import { ResizableImage } from 'tiptap-extension-resizable-image';
import 'tiptap-extension-resizable-image/styles.css';
import Image from '@tiptap/extension-image';
// basic Image removed to prevent duplicate; ResizableImage retains resize functionality
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from "@/components/ui/context-menu";

// Revert: remove custom Callout extension to restore working buttons

// Types
interface NotePage {
  id: string;
  title: string;
  content: string;
  createdAt: Date;
  lastUpdated: Date;
  parentId?: string | null;
}

// API service functions for global notes
const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';

const getAuthHeaders = () => ({
  'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
  'Content-Type': 'application/json'
});

const notesApi = {
  // Load all notes from database
  async loadNotes(): Promise<NotePage[]> {
    const response = await fetch(`${apiUrl}/api/notes`, {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
    });
    if (!response.ok) throw new Error('Failed to load notes');
    
    const notes = await response.json();
    return notes.map((note: any) => ({
      id: note.id,
      title: note.title,
      content: note.content,
      parentId: note.parent_id,
      createdAt: new Date(note.created_at),
      lastUpdated: new Date(note.updated_at)
    }));
  },

  // Create new note
  async createNote(title: string, content: string, parentId?: string): Promise<NotePage> {
    const response = await fetch(`${apiUrl}/api/notes`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ title, content, parentId: parentId || null })
    });
    if (!response.ok) throw new Error('Failed to create note');
    
    const note = await response.json();
    return {
      id: note.id,
      title: note.title,
      content: note.content,
      parentId: note.parent_id,
      createdAt: new Date(note.created_at),
      lastUpdated: new Date(note.updated_at)
    };
  },

  // Update existing note
  async updateNote(id: string, title: string, content: string): Promise<NotePage> {
    console.log('[notesApi] Updating note:', { id, title: title.substring(0, 30), contentLength: content.length });
    
    const response = await fetch(`${apiUrl}/api/notes/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ title, content })
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('[notesApi] Update failed:', response.status, errorText);
      throw new Error(`Failed to update note: ${response.status} ${errorText}`);
    }
    
    const note = await response.json();
    console.log('[notesApi] Update response:', note);
    
    return {
      id: note.id,
      title: note.title,
      content: note.content,
      parentId: note.parent_id,
      createdAt: new Date(note.created_at),
      lastUpdated: new Date(note.updated_at)
    };
  },

  // Delete note
  async deleteNote(id: string): Promise<void> {
    const response = await fetch(`${apiUrl}/api/notes/${id}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
    });
    if (!response.ok) throw new Error('Failed to delete note');
  },

  // Upload image to MinIO and get URL
  async uploadImage(noteId: string, file: File): Promise<string> {
    const formData = new FormData();
    formData.append('image', file);
    
    const response = await fetch(`${apiUrl}/api/notes/${noteId}/images`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` },
      body: formData
    });
    
    if (!response.ok) throw new Error('Failed to upload image');
    
    const result = await response.json();
    return result.imageUrl;
  }
};

interface NotesIconProps {
  onClick: () => void;
}

// Icon Component (integrated)
function NotesIcon({ onClick }: NotesIconProps) {
  return (
    <Button
      variant="outline"
      size="sm"
      className="relative gap-2"
      onClick={onClick}
      aria-label="Open notes"
    >
      <FileText className="h-4 w-4" />
      <span className="text-sm font-medium">Notes</span>
    </Button>
  );
}

  interface NotesPanelProps {
  showPagesList?: boolean;
  onTogglePagesList?: () => void;
  onOpenChange?: (open: boolean) => void;
}

// Export the toggle button for use in the header
export function NotesToggleButton({ showPagesList, onToggle }: { 
  showPagesList: boolean; 
  onToggle: () => void;
}) {
  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={onToggle}
      className="h-8 w-8"
      aria-label={showPagesList ? "Collapse notes sidebar" : "Expand notes sidebar"}
    >
      {showPagesList ? <PanelLeftClose className="h-4 w-4" /> : <PanelLeft className="h-4 w-4" />}
    </Button>
  );
}

// Main unified component
export default function NotesPanel({ 
  showPagesList: externalShowPagesList,
  onTogglePagesList,
  onOpenChange
}: NotesPanelProps = {}) {
  console.log('[NotesPanel] Component loaded - Version 3.0 with optimized layout');
  
  // Get authentication status
  const { isAuthenticated, token } = useAuth();
  
  // State
  const [open, setOpen] = useState(false);
  const [pages, setPages] = useState<NotePage[]>([]);
  const [selectedPageId, setSelectedPageId] = useState<string | null>(null);
  const [internalShowPagesList, setInternalShowPagesList] = useState(true);
  const showPagesList = externalShowPagesList ?? internalShowPagesList;
  const [isMobile, setIsMobile] = useState(false);
  const [drawerWidth, setDrawerWidth] = useState(50);
  const [isResizing, setIsResizing] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<NotePage | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Handle open state changes
  const handleSetOpen = (newOpen: boolean) => {
    setOpen(newOpen);
    onOpenChange?.(newOpen);
  };

  // Editor state
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [localTitle, setLocalTitle] = useState("");
  const [showSearch, setShowSearch] = useState(false);
  const [editorSearchQuery, setEditorSearchQuery] = useState("");
  const [focusMode, setFocusMode] = useState(false);
  const [tableRows, setTableRows] = useState(3); // New state for table rows
  const [tableCols, setTableCols] = useState(3); // New state for table columns
  const [expandedIds, setExpandedIds] = useState<Record<string, boolean>>({});
  const draggingIdRef = useRef<string | null>(null);

  // Refs
  const drawerRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();
  const debouncedUpdate = useRef<(id: string, updates: Partial<NotePage>) => void>();
  const pagesRef = useRef<NotePage[]>([]);

  // keep reference up-to-date whenever pages array changes
  useEffect(() => {
    pagesRef.current = pages;
  }, [pages]);

  // Initialize debouncedUpdate once
  useEffect(() => {
    if (!debouncedUpdate.current) {
      debouncedUpdate.current = debounce(async (id: string, updates: Partial<NotePage>) => {
        console.log('[GlobalNotesPanel] debouncedUpdate called:', { id, updates });
    try {
      setSaving(true);
      const currentPage = pagesRef.current.find(p => p.id === id);
          if (!currentPage) {
            console.error('[GlobalNotesPanel] Current page not found for id:', id);
            return;
          }
      const title = updates.title !== undefined ? updates.title : currentPage.title;
      const content = updates.content !== undefined ? updates.content : currentPage.content;
          console.log('[GlobalNotesPanel] Sending to API:', { id, title: title.substring(0, 50), contentLength: content.length });
      const updatedNote = await notesApi.updateNote(id, title, content);
          console.log('[GlobalNotesPanel] API response:', updatedNote);
          setPages(currentPages => currentPages.map(page => page.id === id ? { ...updatedNote, lastUpdated: new Date() } : page));
    } catch (error) {
          console.error('[GlobalNotesPanel] Failed to auto-save:', error);
          toast({ title: 'Auto-save Failed', description: 'Changes may not be saved. Please check your connection.', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
      }, 2000);
    }
  }, [toast]);

  // Computed values
  const noteCount = pages.length;
  const selectedPage = pages.find(p => p.id === selectedPageId);
  const filteredPages = pages.filter(page => 
    page.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // TipTap Editor
  // Add image upload helper before editor config
  const uploadAndInsertImage = async (file: File): Promise<boolean> => {
    if (!selectedPageId) {
      toast({ title: "Error", description: "Please select a note first", variant: "destructive" });
      return false;
    }

    try {
      toast({ title: "Uploading...", description: "Uploading image to server..." });
      const imageUrl = await notesApi.uploadImage(selectedPageId, file);
      const ok = editor?.chain().focus().setImage({ src: imageUrl }).run();
      if (ok) {
        toast({ title: "Image uploaded!", description: "Image inserted successfully." });
      } else {
        toast({ title: "Insert failed", description: "Could not insert image.", variant: "destructive" });
      }
      return ok || false;
    } catch (error) {
      console.error('Image upload failed:', error);
      toast({ title: "Upload failed", description: "Could not upload image.", variant: "destructive" });
      return false;
    }
  };

  // Editor config starts here
  const editor = useEditor({
    extensions: [
      // 1. Disable StarterKit's conflicting modules to prevent conflicts.
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
        bulletList: false,
        orderedList: false,
        listItem: false,
        // Disable built-in extensions that we configure separately
        link: false,
        // Disable underline to prevent conflicts with our custom Underline extension
        underline: false,
        image: false,
      }),

      // 2. Re-add standard list modules explicitly for reliable behavior.
      BulletList.configure({
        HTMLAttributes: { class: 'list-disc pl-6 my-2' },
      }),
      OrderedList.configure({
        HTMLAttributes: { class: 'list-decimal pl-6 my-2' },
      }),
      ListItem,

      // Foundational extensions
      Underline,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-primary underline cursor-pointer'
        }
      }),
      Highlight.configure({
        multicolor: false,
        HTMLAttributes: {
          class: 'bg-yellow-200 dark:bg-yellow-900/50'
        }
      }),
      Image.configure({ allowBase64: true, HTMLAttributes: { class: 'max-w-full h-auto' } }),
      ResizableImage.configure({ allowBase64: true }),
      Table.configure({
        resizable: true,
        HTMLAttributes: {
          class: 'border-collapse border border-border'
        }
      }),
      TableRow,
      TableHeader.configure({
        HTMLAttributes: {
          class: 'border border-border px-4 py-2 bg-muted font-medium text-left'
        }
      }),
      TableCell.configure({
        HTMLAttributes: {
          class: 'border border-border px-4 py-2'
        }
      }),

      // 3. Configure TaskList and TaskItem with unique, isolated class names.
      TaskList.configure({
        HTMLAttributes: {
          class: 'vbca-task-list',
        },
      }),
      TaskItem.configure({
        nested: true,
        HTMLAttributes: {
          class: 'vbca-task-item',
        },
      }),
    ],
    content: selectedPage?.content || '',
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none focus:outline-none min-h-full',
        dir: 'ltr'
      },
      handlePaste: async (view, event) => {
        const dt = event.clipboardData;
        if (!dt) return false;

        // Handle image files
        const fileFromFiles = Array.from(dt.files || []).find(f => f.type?.startsWith('image/'));
        if (fileFromFiles) {
          event.preventDefault();
          return await uploadAndInsertImage(fileFromFiles);
        }

        // Handle pasted image URLs or HTML (keep existing logic if needed)
        const tryInsert = (src: string) => {
          if (!editor) return false;
          // Prefer the image command if available
          const ran = editor.chain().focus().setImage({ src }).run();
          if (ran) return true;
          // Try explicit node insertions
          const insertedResizable = editor.commands.insertContent({ type: 'resizableImage', attrs: { src } });
          if (insertedResizable) return true;
          const insertedImage = editor.commands.insertContent({ type: 'image', attrs: { src } });
          if (insertedImage) return true;
          return false;
        };

        // 2) Check items API
        const items = Array.from(dt.items || []);
        const imageItem = items.find(item => item.type && item.type.startsWith('image/'));
        if (imageItem) {
          event.preventDefault();
          const file = imageItem.getAsFile();
          if (file) {
            return await uploadAndInsertImage(file);
          }
          return true;
        }

        // 3) Fallback: pasted HTML with <img src="...">
        const html = dt.getData('text/html');
        if (html && html.includes('<img')) {
          const match = html.match(/<img[^>]+src=["']([^"']+)["']/i);
          const src = match?.[1];
          if (src) {
            event.preventDefault();
            const ok = tryInsert(src);
            if (ok) {
              toast({ title: "Image pasted!", description: "Image inserted from HTML." });
            } else {
              toast({ title: "Paste failed", description: "Could not insert image node.", variant: 'destructive' });
            }
            return true;
          }
        }

        return false;
      },
      handleDrop: async (view, event) => {
        const files = Array.from(event.dataTransfer?.files || []);
        const imageFile = files.find(file => file.type.startsWith('image/'));
        
        if (imageFile) {
          event.preventDefault();
          return await uploadAndInsertImage(imageFile);
        }
        return false;
      },
      handleKeyDown: (view, event) => {
        // Support Ctrl+Y (Windows) as redo in addition to Mod-Shift-Z
        const isCtrlOrMeta = event.ctrlKey || event.metaKey;
        if (isCtrlOrMeta && !event.shiftKey && event.key.toLowerCase() === 'y') {
          event.preventDefault();
          editor?.chain().focus().redo().run();
          return true;
        }
        return false;
      }
    },
    onUpdate: ({ editor }) => {
      console.log('[Editor] onUpdate triggered', { 
        selectedPageId, 
        hasSelectedPage: !!selectedPage,
        hasDebouncedUpdate: !!debouncedUpdate.current,
        content: editor.getHTML().substring(0, 100) 
      });
      
      if (selectedPageId && selectedPage) {
        // Update local state immediately
        updatePage(selectedPageId, { content: editor.getHTML() });
        // Trigger API save - check if debouncedUpdate is ready
        if (debouncedUpdate.current && typeof debouncedUpdate.current === 'function') {
          console.log('[Editor] Calling debouncedUpdate for content change');
        debouncedUpdate.current(selectedPageId, { content: editor.getHTML() });
        } else {
          console.error('[Editor] debouncedUpdate not available!', { 
            current: debouncedUpdate.current,
            type: typeof debouncedUpdate.current 
          });
        }
      } else {
        console.warn('[Editor] onUpdate called but no selectedPageId or selectedPage', {
          selectedPageId,
          hasSelectedPage: !!selectedPage
        });
      }
    }
  });

  // Update editor content when page changes
  useEffect(() => {
    if (editor && selectedPage) {
      try {
        // Ensure content is not empty or just whitespace
        const content = selectedPage.content && selectedPage.content.trim() !== '' 
          ? selectedPage.content 
          : '<p></p>';
        
        if (editor.getHTML() !== content) {
          editor.commands.setContent(content);
        }
        setLocalTitle(selectedPage.title || 'Untitled Page');
      } catch (error) {
        console.error('[GlobalNotesPanel] Error setting editor content:', error);
        // Fallback: clear and set basic content
        editor.commands.clearContent();
        editor.commands.insertContent('<p></p>');
        setLocalTitle(selectedPage.title || 'Untitled Page');
      }
    } else if (editor) {
      editor.commands.clearContent();
      setLocalTitle("");
    }
  }, [selectedPage, editor]);

  // Load data from database API - only when authenticated
  useEffect(() => {
    // Don't try to load notes if not authenticated
    if (!isAuthenticated || !token) {
      setLoading(false);
      return;
    }

    const loadNotes = async () => {
      setLoading(true);
      setError(null);
      try {
        const notes = await notesApi.loadNotes();
        setPages(notes);
      } catch (error) {
        console.error('Failed to load notes:', error);
        setError('Failed to load notes. Please refresh the page.');
        toast({
          title: "Loading Error",
          description: "Failed to load notes. Please refresh the page.",
          variant: "destructive"
        });
      } finally {
        setLoading(false);
      }
    };

    loadNotes();
  }, [isAuthenticated, token, toast]);

  // No localStorage saving needed

  // Mobile detection
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Resize handling
  const handleResizeStart = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  };

  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      const newWidth = Math.max(30, Math.min(80, (window.innerWidth - e.clientX) / window.innerWidth * 100));
      setDrawerWidth(newWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isResizing]);

  // Page management functions
  const createPage = async (parentId: string | null = null) => {
    try {
      const newPage = await notesApi.createNote(
        parentId ? "Untitled Sub-page" : "Untitled Page",
        "<p></p>",
        parentId
      );
      console.log(`[NotesPanel] Creating new page: id=${newPage.id}, parentId=${parentId}`);
      setPages(prev => {
        console.log(`[NotesPanel] Pages before create:`, prev.map(p => ({ id: p.id, title: p.title, parentId: p.parentId })));
        const next = [...prev, newPage];
        const seen: Record<string, boolean> = {};
        const result = next.filter(p => (seen[p.id] ? false : (seen[p.id] = true)));
        console.log(`[NotesPanel] Pages after create:`, result.map(p => ({ id: p.id, title: p.title, parentId: p.parentId })));
        return result;
      });
      setSelectedPageId(newPage.id);
      editor?.commands.setContent(newPage.content);
      setLocalTitle(newPage.title);
      toast({
        title: "Page Created",
        description: "New note page created successfully."
      });
      if (parentId) {
        setExpandedIds(prev => ({ ...prev, [parentId]: true }));
      }
    } catch (error) {
      console.error('Failed to create page:', error);
      toast({
        title: "Creation Error",
        description: "Failed to create new page. Please try again.",
        variant: "destructive"
      });
    }
  };

  const updatePage = (id: string, updates: Partial<NotePage>) => {
    setPages(prev => {
      const next = prev.map(page => 
      page.id === id 
        ? { ...page, ...updates, lastUpdated: new Date() }
        : page
      );
      const seen: Record<string, boolean> = {};
      return next.filter(p => (seen[p.id] ? false : (seen[p.id] = true)));
    });
  };

  const movePage = (id: string, newParentId: string | null) => {
    console.log(`[NotesPanel] movePage called: id=${id}, newParentId=${newParentId}`);
    if (id === newParentId) return;
    const pageToMove = pages.find(p => p.id === id);
    if (!pageToMove) return;

    // Prevent loops: a page cannot become a child of itself or its descendants
    const isDescendant = (candidateParentId: string | null, targetId: string): boolean => {
      if (!candidateParentId) return false;
      if (candidateParentId === targetId) return true;
      const parent = pages.find(p => p.id === candidateParentId);
      return parent ? isDescendant(parent.parentId ?? null, targetId) : false;
    };
    if (isDescendant(newParentId, id)) {
      console.log(`[NotesPanel] Prevented loop: ${id} cannot be child of ${newParentId}`);
      return;
    }

    // No-op if parent unchanged
    if ((pageToMove.parentId ?? null) === (newParentId ?? null)) {
      console.log(`[NotesPanel] No change needed, parent already ${newParentId}`);
      return;
    }

    console.log(`[NotesPanel] Moving page ${id} from parent ${pageToMove.parentId} to ${newParentId}`);
    updatePage(id, { parentId: newParentId });
    if (newParentId) setExpandedIds(prev => ({ ...prev, [newParentId]: true }));
  };

  const deletePage = async () => {
    if (!itemToDelete) return;
    
    try {
      await notesApi.deleteNote(itemToDelete.id);
      setPages(pages.filter(p => p.id !== itemToDelete.id));
      if (selectedPageId === itemToDelete.id) {
        setSelectedPageId(null);
        editor?.commands.clearContent();
      }
      setDeleteDialogOpen(false);
      setItemToDelete(null);
      toast({
        title: "Page deleted",
        description: "The page has been permanently deleted.",
      });
    } catch (error) {
      console.error('Failed to delete page:', error);
      toast({
        title: "Delete Error",
        description: "Failed to delete page. Please try again.",
        variant: "destructive"
      });
    }
  };

  const duplicatePage = (page: NotePage) => {
    const newPage: NotePage = {
      ...page,
      id: (typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`),
      title: `${page.title} (Copy)`,
      createdAt: new Date(),
      lastUpdated: new Date(),
    };
    setPages(prev => {
      const next = [...prev, newPage];
      const seen: Record<string, boolean> = {};
      return next.filter(p => (seen[p.id] ? false : (seen[p.id] = true)));
    });
    setSelectedPageId(newPage.id);
  };

  const handleTitleSubmit = () => {
    console.log('[Title] handleTitleSubmit called', { 
      selectedPageId, 
      localTitle, 
      hasSelectedPage: !!selectedPage,
      hasDebouncedUpdate: !!debouncedUpdate.current 
    });
    
    if (selectedPageId && selectedPage) {
      // Update local state immediately for UI responsiveness
      updatePage(selectedPageId, { title: localTitle });
      // Trigger API save
      if (debouncedUpdate.current && typeof debouncedUpdate.current === 'function') {
        console.log('[Title] Calling debouncedUpdate for title change');
      debouncedUpdate.current(selectedPageId, { title: localTitle });
      } else {
        console.error('[Title] debouncedUpdate not available!', { 
          current: debouncedUpdate.current,
          type: typeof debouncedUpdate.current 
        });
      }
    }
    setIsEditingTitle(false);
  };

  const insertCallout = (type: 'info' | 'warning' | 'success') => {
    const icons: Record<string, string> = { info: '💡', warning: '⚠️', success: '✅' };
    // Fallback: insert styled div with inline styles to ensure visibility
    const styles: Record<string, { bg: string; border: string; color: string }> = {
      info:   { bg: 'rgb(239 246 255)', border: '4px solid rgb(59 130 246)',  color: 'rgb(30 58 138)' },
      warning:{ bg: 'rgb(255 251 235)', border: '4px solid rgb(245 158 11)',  color: 'rgb(146 64 14)' },
      success:{ bg: 'rgb(240 253 244)', border: '4px solid rgb(34 197 94)',   color: 'rgb(20 83 45)' },
    };
    const s = styles[type];
    // Wrapper around EditorContent guarantees px-4; use -16px to break out
    const html = `<div style="background:${s.bg};color:${s.color};border-left:${s.border};margin:1rem -16px;padding:1rem 2rem;min-height:3.5rem;display:flex;align-items:center;gap:.5rem;">${icons[type]} Type your ${type} message here...</div>`;
    editor?.chain().focus().insertContent(html).run();
  };

  const copyAsMarkdown = () => {
    if (!selectedPage) return;
    
    let markdown = selectedPage.content
      .replace(/<h1[^>]*>(.*?)<\/h1>/gi, '# $1\n')
      .replace(/<h2[^>]*>(.*?)<\/h2>/gi, '## $1\n')
      .replace(/<h3[^>]*>(.*?)<\/h3>/gi, '### $1\n')
      .replace(/<strong[^>]*>(.*?)<\/strong>/gi, '**$1**')
      .replace(/<b[^>]*>(.*?)<\/b>/gi, '**$1**')
      .replace(/<em[^>]*>(.*?)<\/em>/gi, '*$1*')
      .replace(/<i[^>]*>(.*?)<\/i>/gi, '*$1*')
      .replace(/<u[^>]*>(.*?)<\/u>/gi, '$1')
      .replace(/<code[^>]*>(.*?)<\/code>/gi, '`$1`')
      .replace(/<li[^>]*>(.*?)<\/li>/gi, '- $1\n')
      .replace(/<img[^>]*src="([^"]*)"[^>]*>/gi, '![]($1)\n')
      .replace(/<[^>]+>/g, '');
    
    navigator.clipboard.writeText(markdown);
    toast({ title: "Copied as Markdown", description: "The page content has been copied to your clipboard." });
  };

  const formatDate = (date?: Date | string) => {
    if (!date) return "";
    try {
      const d = typeof date === 'string' ? new Date(date) : date;
      if (isNaN(d.getTime())) {
        return "Invalid Date";
      }
      return d.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch (e) {
      return "Invalid Date";
    }
  };

  const toggleExpanded = (id: string) => {
    setExpandedIds(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const getChildren = (parentId: string | null) => pages.filter(p => (p.parentId ?? null) === parentId);

  const isDescendantOf = (candidateDescendantId: string, ancestorId: string): boolean => {
    const candidate = pages.find(p => p.id === candidateDescendantId);
    if (!candidate || !candidate.parentId) return false;
    if (candidate.parentId === ancestorId) return true;
    return isDescendantOf(candidate.parentId, ancestorId);
  };

  const renderPageItem = (page: NotePage, depth: number = 0) => {
    const children = getChildren(page.id);
    const expanded = expandedIds[page.id] !== false; // Default to expanded
    return (
      <div key={page.id}>
        <div
          className={cn(
            "group px-4 py-2 hover:bg-muted/50 cursor-pointer border-b flex items-center",
            selectedPageId === page.id && "bg-muted"
          )}
          style={{ paddingLeft: 12 + depth * 14 }}
          onClick={() => setSelectedPageId(page.id)}
          draggable
          onDragStart={(e) => { 
            draggingIdRef.current = page.id; 
            try { e.dataTransfer.setData('application/x-notes-page-id', page.id); } catch {}
            e.dataTransfer.setData('text/plain', page.id);
            e.dataTransfer.effectAllowed = 'move';
          }}
          onDragEnd={() => { draggingIdRef.current = null; }}
          onDragOver={(e) => { e.preventDefault(); }}
          onDrop={(e) => { 
            e.preventDefault(); 
            e.stopPropagation();
            const draggedId = draggingIdRef.current 
              || e.dataTransfer.getData('application/x-notes-page-id') 
              || e.dataTransfer.getData('text/plain'); 
            if (!draggedId || draggedId === page.id) return; 
            movePage(draggedId, page.id); 
            draggingIdRef.current = null;
          }}
        >
          {children.length > 0 && (
            <button
              className="mr-2 text-sm hover:bg-muted rounded p-1"
              onClick={(e) => { e.stopPropagation(); toggleExpanded(page.id); }}
              title={expanded ? "Click to collapse" : "Click to expand"}
            >
              {expanded ? '▼' : '▶'}
            </button>
          )}
          <div className="flex-1 min-w-0">
            <h4 className="font-medium truncate">{page.title}</h4>
            <p className="text-xs text-muted-foreground mt-1">{formatDate(page.lastUpdated)}</p>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100"
                onClick={(e) => e.stopPropagation()}
              >
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); updatePage(page.id, { title: window.prompt("Rename page:", page.title) || page.title }); }}>
                <Edit3 className="h-4 w-4 mr-2" />
                Rename
              </DropdownMenuItem>
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); duplicatePage(page); }}>
                <Copy className="h-4 w-4 mr-2" />
                Duplicate
              </DropdownMenuItem>
              {/* Make sub-page option - show if there's a sibling above */}
              {(() => {
                const siblings = pages.filter(p => (p.parentId ?? null) === (page.parentId ?? null));
                const currentIndex = siblings.findIndex(p => p.id === page.id);
                return currentIndex > 0 ? (
                  <DropdownMenuItem onClick={(e) => { 
                    e.stopPropagation(); 
                    const newParent = siblings[currentIndex - 1];
                    movePage(page.id, newParent.id);
                    setExpandedIds(prev => ({ ...prev, [newParent.id]: true }));
                    if (searchQuery) setSearchQuery("");
                    toast({ title: "Made sub-page", description: `"${page.title}" is now under "${newParent.title}"` });
                  }}>
                    <ArrowRight className="h-4 w-4 mr-2" />
                    Make sub-page
                  </DropdownMenuItem>
                ) : null;
              })()}
              
              {/* Move up one level - show if page has a parent */}
              {page.parentId && (
                <DropdownMenuItem onClick={(e) => { 
                  e.stopPropagation(); 
                  const parent = pages.find(p => p.id === page.parentId);
                  if (parent && parent.parentId) {
                    movePage(page.id, parent.parentId);
                  } else {
                    movePage(page.id, null);
                  }
                }}>
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Move up one level
                </DropdownMenuItem>
              )}
              <DropdownMenuItem className="text-destructive" onClick={(e) => { e.stopPropagation(); setItemToDelete(page); setDeleteDialogOpen(true); }}>
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        {children.length > 0 && expanded && (
          <div>
            {children.map(child => renderPageItem(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  // Render functions
  const renderEmptyState = () => (
    <div className="flex-1 flex items-center justify-center p-8">
      <div className="text-center space-y-4">
        <FileText className="h-16 w-16 mx-auto text-muted-foreground" />
        <h3 className="text-lg font-semibold">No pages yet</h3>
        <p className="text-sm text-muted-foreground">
          Create your first page to start taking notes
        </p>
        <Button onClick={() => createPage(null)}>
          <Plus className="h-4 w-4 mr-2" />
          Create Page
        </Button>
      </div>
    </div>
  );

  const renderNoPageSelected = () => (
    <div className="flex-1 flex items-center justify-center p-8">
      <div className="text-center space-y-4">
        <FileText className="h-16 w-16 mx-auto text-muted-foreground" />
        <h3 className="text-lg font-semibold">Select a page</h3>
        <p className="text-sm text-muted-foreground">
          Choose a page from the list to start editing
        </p>
      </div>
    </div>
  );

  const renderEditor = () => (
    <div className="flex flex-1 flex-col h-full relative">
      {/* Optimized Header with Title and Close Button */}
      {!focusMode && (
        <div className="sticky top-0 z-10 bg-background border-b">
          {/* Title Row with Close Button */}
          <div className="flex items-center h-12 px-4 border-b">
            {/* Toggle button on the left */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onTogglePagesList ? onTogglePagesList() : setInternalShowPagesList(!showPagesList)}
              className="h-8 w-8 mr-2"
              aria-label={showPagesList ? "Collapse sidebar" : "Expand sidebar"}
            >
              {showPagesList ? <PanelLeftClose className="h-4 w-4" /> : <PanelLeft className="h-4 w-4" />}
            </Button>
            
            <div className="flex-1 flex justify-center">
              {isEditingTitle ? (
                <Input
                  value={localTitle}
                  onChange={(e) => setLocalTitle(e.target.value)}
                  onBlur={handleTitleSubmit}
                  onKeyPress={(e) => e.key === 'Enter' && handleTitleSubmit()}
                  className="text-base font-medium h-8 px-3 max-w-md text-center"
                  autoFocus
                />
              ) : (
                <h2 
                  className="text-base font-medium cursor-pointer hover:text-muted-foreground transition-colors px-3"
                  onClick={() => setIsEditingTitle(true)}
                >
                  {selectedPage?.title || 'Untitled Page'}
                </h2>
              )}
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 ml-auto"
              onClick={() => handleSetOpen(false)}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          
          {/* Toolbar Row */}
          <div className="px-4 h-10 flex items-center gap-2">
            {/* Undo/Redo */}
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0"
                onClick={() => editor?.chain().focus().undo().run()}
                title="Undo (Ctrl+Z / Cmd+Z)"
                disabled={!editor?.can().undo()}
              >
                <Undo2 className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0"
                onClick={() => editor?.chain().focus().redo().run()}
                title="Redo (Ctrl+Y / Cmd+Shift+Z)"
                disabled={!editor?.can().redo()}
              >
                <Redo2 className="h-4 w-4" />
              </Button>
            </div>

            <div className="h-5 w-px bg-border" />

            {/* Text formatting group */}
            <div className="flex items-center">
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-8 w-8 p-0"
                onClick={() => editor?.chain().focus().toggleHeading({ level: 1 }).run()}
                data-active={editor?.isActive('heading', { level: 1 })}
                title="Heading 1"
              >
                <span className="text-sm font-medium">H₁</span>
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-8 w-8 p-0"
                onClick={() => editor?.chain().focus().toggleHeading({ level: 2 }).run()}
                data-active={editor?.isActive('heading', { level: 2 })}
                title="Heading 2"
              >
                <span className="text-sm font-medium">H₂</span>
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-8 w-8 p-0"
                onClick={() => editor?.chain().focus().toggleHeading({ level: 3 }).run()}
                data-active={editor?.isActive('heading', { level: 3 })}
                title="Heading 3"
              >
                <span className="text-sm font-medium">H₃</span>
              </Button>
            </div>

            <div className="h-5 w-px bg-border" />

            {/* Basic formatting */}
            <div className="flex items-center gap-1">
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-8 w-8 p-0"
                onClick={() => editor?.chain().focus().toggleBold().run()}
                data-active={editor?.isActive('bold')}
                title="Bold"
              >
                <Bold className="h-4 w-4" />
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-8 w-8 p-0"
                onClick={() => editor?.chain().focus().toggleItalic().run()}
                data-active={editor?.isActive('italic')}
                title="Italic"
              >
                <Italic className="h-4 w-4" />
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-8 w-8 p-0"
                onClick={() => editor?.chain().focus().toggleUnderline().run()}
                data-active={editor?.isActive('underline')}
                title="Underline"
              >
                <UnderlineIcon className="h-4 w-4" />
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-8 w-8 p-0"
                onClick={() => editor?.chain().focus().toggleCode().run()}
                data-active={editor?.isActive('code')}
                title="Code"
              >
                <Code className="h-4 w-4" />
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-8 w-8 p-0"
                onClick={() => {
                  const url = window.prompt('Enter URL:');
                  if (url) {
                    editor?.chain().focus().setLink({ href: url }).run();
                  }
                }}
                data-active={editor?.isActive('link')}
                title="Link"
              >
                <LinkIcon className="h-4 w-4" />
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-8 w-8 p-0"
                onClick={() => editor?.chain().focus().toggleHighlight().run()}
                data-active={editor?.isActive('highlight')}
                title="Highlight"
              >
                <Highlighter className="h-4 w-4" />
              </Button>
            </div>

            <div className="h-5 w-px bg-border" />

            {/* Lists and structure */}
            <div className="flex items-center gap-1">
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-8 w-8 p-0"
                onClick={() => editor?.chain().focus().toggleBulletList().run()}
                data-active={editor?.isActive('bulletList')}
                title="Bullet list"
              >
                <List className="h-4 w-4" />
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-8 w-8 p-0"
                onClick={() => editor?.chain().focus().toggleOrderedList().run()}
                data-active={editor?.isActive('orderedList')}
                title="Numbered list"
              >
                <ListOrdered className="h-4 w-4" />
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-8 w-8 p-0"
                onClick={() => editor?.chain().focus().toggleTaskList().run()}
                data-active={editor?.isActive('taskList')}
                title="Checklist"
              >
                <CheckSquare className="h-4 w-4" />
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-8 w-8 p-0"
                    title="Insert table"
                  >
                    <TableIcon className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  {[2, 3, 4, 5].map((rows) => (
                    [2, 3, 4, 5].map((cols) => (
                      <DropdownMenuItem
                        key={`${rows}x${cols}`}
                onClick={() => {
                    editor?.chain()
                      .focus()
                            .insertTable({ rows, cols, withHeaderRow: true })
                      .run();
                        }}
                      >
                        {rows} x {cols} Table
                      </DropdownMenuItem>
                    ))
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            <div className="h-5 w-px bg-border" />

            {/* Callouts */}
            <div className="flex items-center gap-1">
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-8 w-8 p-0"
                onClick={() => insertCallout('info')}
                title="Info callout"
              >
                <Info className="h-4 w-4" />
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-8 w-8 p-0"
                onClick={() => insertCallout('warning')}
                title="Warning callout"
              >
                <AlertCircle className="h-4 w-4" />
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-8 w-8 p-0"
                onClick={() => insertCallout('success')}
                title="Success callout"
              >
                <CheckCircle className="h-4 w-4" />
              </Button>
            </div>

            {/* Action buttons - moved to right */}
            <div className="flex items-center gap-1 ml-auto">
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-8 w-8 p-0"
                onClick={() => setShowSearch(!showSearch)}
                title="Search"
              >
                <SearchIcon className="h-4 w-4" />
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-8 w-8 p-0"
                onClick={copyAsMarkdown}
                title="Copy as Markdown"
              >
                <Copy className="h-4 w-4" />
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-8 w-8 p-0"
                onClick={() => setFocusMode(!focusMode)}
                title={focusMode ? "Exit focus mode" : "Enter focus mode"}
              >
                <Maximize2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Search Bar */}
      {showSearch && !focusMode && (
        <div className="border-b px-4 py-2">
          <Input
            placeholder="Search in page..."
            value={editorSearchQuery}
            onChange={(e) => setEditorSearchQuery(e.target.value)}
            className="max-w-md h-9"
          />
        </div>
      )}

      {/* Editor */}
      <div className={cn(
        "flex-1 overflow-y-auto relative"
      )}>
        <ContextMenu>
          {/* Wrapper enforces consistent horizontal padding in all modes */}
          <ContextMenuTrigger>
            <div className="px-4 py-2">
              <ErrorBoundary
                fallback={<div className="p-4 text-red-500">Error loading note content. Please try again.</div>}
                onError={(error) => console.error('Editor render error:', error)}
              >
              <EditorContent 
                editor={editor} 
                className={cn(
                  "prose prose-sm max-w-none dark:prose-invert"
                )}
              />
              </ErrorBoundary>
            </div>
          </ContextMenuTrigger>
          <ContextMenuContent>
            {editor?.isActive('table') && (
              <>
                <ContextMenuItem onClick={() => editor.chain().focus().addRowAfter().run()}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Row Below
                </ContextMenuItem>
                <ContextMenuItem onClick={() => editor.chain().focus().deleteRow().run()}>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Row
                </ContextMenuItem>
                <ContextMenuItem onClick={() => editor.chain().focus().addColumnAfter().run()}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Column After
                </ContextMenuItem>
                <ContextMenuItem onClick={() => editor.chain().focus().deleteColumn().run()}>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Column
                </ContextMenuItem>
                <ContextMenuItem onClick={() => editor.chain().focus().deleteTable().run()}>
                  <X className="h-4 w-4 mr-2" />
                  Delete Table
                </ContextMenuItem>
              </>
            )}
            <ContextMenuItem>Copy</ContextMenuItem>
            <ContextMenuItem>Paste</ContextMenuItem>
          </ContextMenuContent>
        </ContextMenu>
      </div>
    </div>
  );

  return (
    <>
      <style dangerouslySetInnerHTML={{__html: `
        /* ===== FINAL CHECKLIST FIX ===== */

        /* 1. Reset the task list container. */
        .vbca-task-list {
          list-style: none !important;
          padding: 0 !important;
          margin: 0.5rem 0 !important;
        }

        /* 2. Style the task item using Flexbox for alignment. */
        .vbca-task-item {
          display: flex !important;
          align-items: flex-start !important; /* Align checkbox to the top of the text block */
          gap: 0.5rem !important;
          margin: 0.25rem 0 !important;
          padding: 0 !important;
        }

        /* 3. The label contains the checkbox. Prevent it from growing. */
        .vbca-task-item > label {
          flex-shrink: 0;
          margin-top: 0.2em; /* Nudge checkbox down slightly to align with text */
        }

        /* 4. The div contains the text content. Allow it to grow. */
        .vbca-task-item > div {
          flex-grow: 1;
        }

        /* 5. Ensure paragraphs within the text container have no extra margin. */
        .vbca-task-item > div p {
          margin: 0 !important;
        }
      `}} />

      
      {/* Icon in header */}
      <NotesIcon onClick={() => handleSetOpen(true)} />

      {/* Main drawer/panel */}
      <Sheet open={open} onOpenChange={handleSetOpen}>
        <SheetContent 
          ref={drawerRef}
          className={cn(
            "flex flex-col p-0",
            isMobile ? "w-full" : "sm:max-w-none",
            focusMode && "sm:max-w-full"
          )}
          style={!isMobile && !focusMode ? { width: `${drawerWidth}%` } : undefined}
        >
          {/* Resize handle */}
          {!isMobile && !focusMode && (
            <div
              className="absolute left-0 top-0 h-full w-1 cursor-ew-resize hover:bg-primary/50 transition-colors z-10"
              onMouseDown={handleResizeStart}
            />
          )}

          {/* Header - Exit focus mode button when in focus */}
          {focusMode && (
            <Button
              variant="ghost"
              size="sm"
              className="absolute top-4 right-4 z-50"
              onClick={() => setFocusMode(false)}
            >
              <X className="h-4 w-4 mr-2" />
              Exit Focus Mode
            </Button>
          )}
          
          {/* Mobile back button */}
          {isMobile && pages.length > 0 && selectedPageId && !focusMode && (
            <div className="flex items-center px-4 py-2 border-b">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setSelectedPageId(null)}
                  className="h-8 w-8"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
          </div>
          )}

          {/* Content */}
          <div className="flex flex-1 overflow-hidden">
            {/* Sidebar - Notes List - Hidden in focus mode */}
            {showPagesList && !isMobile && !focusMode && (
              <div className="w-72 border-r flex flex-col">
                {/* Header Section */}
                <div className="px-4 py-3 border-b">
                  <div className="flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    <h3 className="font-semibold text-base">Notebook</h3>
                  </div>
                </div>
                
                {/* Search and Actions */}
                <div className="p-4 space-y-3 border-b">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search pages..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-9 h-8"
                    />
                </div>
                
                            <Button
                    className="w-full" 
                              size="sm"
                    variant="secondary"
                    onClick={() => createPage(null)}
                            >
                    <Plus className="h-4 w-4 mr-2" />
                    New Page
                            </Button>
                </div>
                
                {/* Pages list */}
                <div className="flex-1 overflow-y-auto"
                     onDragOver={(e) => e.preventDefault()}
                     onDrop={(e) => { 
                       e.preventDefault(); 
                                e.stopPropagation();
                       // Only handle drop if the actual root container is targeted
                       if (e.target !== e.currentTarget) return;
                       const draggedId = draggingIdRef.current 
                         || e.dataTransfer.getData('application/x-notes-page-id') 
                         || e.dataTransfer.getData('text/plain'); 
                       if (draggedId) movePage(draggedId, null); 
                       draggingIdRef.current = null;
                     }}
                >
                  {getChildren(null)
                    .filter(page => page.title.toLowerCase().includes(searchQuery.toLowerCase()))
                    .map(page => renderPageItem(page, 0))}
                </div>
                
                {/* Clear All Pages - Bottom Section */}
                {pages.length > 0 && (
                  <div className="p-4 border-t">
                    <Button 
                      className="w-full" 
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                          const confirmation = window.prompt(
                            `This will permanently delete all ${pages.length} pages.\n\nTo confirm, type exactly: DELETE ALL PAGES`
                          );
                          if (confirmation === "DELETE ALL PAGES") {
                            setPages([]);
                            setSelectedPageId(null);
                            localStorage.removeItem("globalNotes");
                            toast({ title: "All pages deleted", description: "Starting fresh with no pages." });
                          } else if (confirmation !== null) {
                            toast({ 
                              title: "Deletion cancelled", 
                              description: "Text didn't match. Pages were not deleted.",
                              variant: "destructive" 
                            });
                          }
                              }}
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                        Clear All Pages
                      </Button>
                      </div>
                  )}
              </div>
            )}

            {/* Mobile Pages List or Editor */}
            {isMobile && !focusMode ? (
              selectedPageId ? (
                <div className="flex-1 flex flex-col">
                  {renderEditor()}
                </div>
              ) : (
                <div className="flex-1 flex flex-col">
                  {/* Header Section */}
                  <div className="px-4 py-3 border-b">
                    <div className="flex items-center gap-2">
                      <FileText className="h-5 w-5" />
                      <h3 className="font-semibold text-base">Notebook</h3>
                    </div>
                  </div>
                  
                  {/* Search and Actions */}
                  <div className="p-4 space-y-3 border-b">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Search pages..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-9 h-8"
                      />
                    </div>
                    
                              <Button
                      className="w-full" 
                                size="sm"
                      variant="secondary"
                      onClick={() => createPage(null)}
                              >
                      <Plus className="h-4 w-4 mr-2" />
                      New Page
                              </Button>
                  </div>
                  
                  {/* Pages list */}
                  <div className="flex-1 overflow-y-auto"
                       onDragOver={(e) => e.preventDefault()}
                       onDrop={(e) => { 
                         e.preventDefault(); 
                                  e.stopPropagation();
                         if (e.target !== e.currentTarget) return;
                         const draggedId = draggingIdRef.current 
                           || e.dataTransfer.getData('application/x-notes-page-id') 
                           || e.dataTransfer.getData('text/plain'); 
                         if (draggedId) movePage(draggedId, null); 
                         draggingIdRef.current = null;
                       }}
                  >
                    {getChildren(null)
                      .filter(page => page.title.toLowerCase().includes(searchQuery.toLowerCase()))
                      .map(page => renderPageItem(page, 0))}
                  </div>
                  
                  {/* Clear All Pages - Bottom Section */}
                  {pages.length > 0 && (
                    <div className="p-4 border-t">
                      <Button 
                        className="w-full" 
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                            const confirmation = window.prompt(
                              `This will permanently delete all ${pages.length} pages.\n\nTo confirm, type exactly: DELETE ALL PAGES`
                            );
                            if (confirmation === "DELETE ALL PAGES") {
                              setPages([]);
                              setSelectedPageId(null);
                              localStorage.removeItem("globalNotes");
                              toast({ title: "All pages deleted", description: "Starting fresh with no pages." });
                            } else if (confirmation !== null) {
                              toast({ 
                                title: "Deletion cancelled", 
                                description: "Text didn't match. Pages were not deleted.",
                                variant: "destructive" 
                              });
                            }
                                }}
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                          Clear All Pages
                        </Button>
                        </div>
                    )}
                </div>
              )
            ) : (
               /* Main Content Area */
              <div className="flex-1 flex flex-col">
                {loading ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-muted-foreground">Loading notes...</div>
                  </div>
                ) : error ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-destructive">{error}</div>
                  </div>
                ) : (
                  pages.length === 0 ? (
                    renderEmptyState()
                  ) : selectedPage ? (
                    renderEditor()
                  ) : (
                    renderNoPageSelected()
                  )
                )}
              </div>
            )}
          </div>

          {/* Delete Dialogs */}
          <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete Page</AlertDialogTitle>
                <AlertDialogDescription>
                  Are you sure you want to delete "{itemToDelete?.title}"? This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={deletePage} className="bg-destructive text-destructive-foreground">
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </SheetContent>
      </Sheet>
    </>
  );
}